// PostgreSQL procedural feed (ProceduralBodyIndexing Slice 2.1).
//
// Enumerates standalone procedures (`pg_proc.prokind = 'p'`) + triggers
// (`pg_trigger`). Procedure bodies come back from `pg_get_functiondef`
// wrapped in `CREATE PROCEDURE ... AS $tag$ … $tag$` plus the plpgsql
// `[DECLARE …] BEGIN … END[;]` shell. We strip the dollar-quoted wrapper
// and replace the outer BEGIN/END with spaces (preserves byte offsets) so
// the universal scanner can split inner statements on `;` and classify
// embedded SELECT / SELECT INTO as `kind === 'select'`. Triggers carry
// CREATE TRIGGER text via `pg_get_triggerdef` — they rarely yield walked
// refs (the trigger function's body lives in pg_proc), but they're
// enumerated per PRD so find-references records the trigger object
// structurally.

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

const SYSTEM_SCHEMAS = `('pg_catalog', 'information_schema', 'pg_toast')`;

const PROC_SQL = `SELECT n.nspname AS schema, p.proname AS name,
  pg_get_functiondef(p.oid) AS body
FROM pg_catalog.pg_proc p
JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
WHERE p.prokind = 'p'
  AND n.nspname NOT IN ${SYSTEM_SCHEMAS}`;

const TRIGGER_SQL = `SELECT n.nspname AS schema, t.tgname AS name,
  pg_get_triggerdef(t.oid) AS body
FROM pg_catalog.pg_trigger t
JOIN pg_catalog.pg_class c ON c.oid = t.tgrelid
JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
WHERE t.tgisinternal = false
  AND n.nspname NOT IN ${SYSTEM_SCHEMAS}`;

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('postgres', objectKind, schema, name, bodyText);
}

export function stripPgProceduralWrapper(body: string): string {
  const m = /\bAS\s+(\$[A-Za-z0-9_]*\$)/i.exec(body);
  if (!m) return body;
  const tag = m[1];
  const innerStart = m.index + m[0].length;
  const closeIdx = body.indexOf(tag, innerStart);
  if (closeIdx < 0) return body;
  let inner = body.slice(innerStart, closeIdx);
  const firstBegin = /\bBEGIN\b/i.exec(inner);
  const lastEnd = /\bEND\b\s*;?\s*$/i.exec(inner);
  if (firstBegin && lastEnd && firstBegin.index < lastEnd.index) {
    const chars = inner.split('');
    for (let i = firstBegin.index; i < firstBegin.index + firstBegin[0].length; i++) chars[i] = ' ';
    for (let i = lastEnd.index; i < lastEnd.index + lastEnd[0].length; i++) chars[i] = ' ';
    inner = chars.join('');
  }
  return inner;
}

export async function listPgsqlProceduralObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [procRows, triggerRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'pgsql',
      label: 'pg-procedures',
      sql: PROC_SQL,
      orderBy: 'p.oid',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'pgsql',
      label: 'pg-triggers',
      sql: TRIGGER_SQL,
      orderBy: 't.oid',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];
  for (const row of procRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('procedure', asString(row.schema), name, stripPgProceduralWrapper(body)));
  }
  for (const row of triggerRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('trigger', asString(row.schema), name, body));
  }
  return out;
}

export async function fetchPgsqlProceduralObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  const s = escapeSqlLiteral(objectName.schema ?? 'public');
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'procedure') {
    const sql = `SELECT pg_get_functiondef(p.oid) AS body
      FROM pg_catalog.pg_proc p
      JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
      WHERE n.nspname = '${s}' AND p.proname = '${n}' AND p.prokind = 'p'`;
    const rows = await fetchRows(connectionId, database, sql, 'pg-procedure-body');
    const raw = asString(rows[0]?.body);
    return raw ? stripPgProceduralWrapper(raw) : null;
  }

  if (objectKind === 'trigger') {
    const sql = `SELECT pg_get_triggerdef(t.oid) AS body
      FROM pg_catalog.pg_trigger t
      JOIN pg_catalog.pg_class c ON c.oid = t.tgrelid
      JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
      WHERE n.nspname = '${s}' AND t.tgname = '${n}' AND t.tgisinternal = false`;
    const rows = await fetchRows(connectionId, database, sql, 'pg-trigger-body');
    return asString(rows[0]?.body) || null;
  }

  return null;
}
